import { DownloadOutlined, PlusOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, message, Modal } from 'antd';
import { isEmpty } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { useSynchronizePorscheMasterDataMutation } from '../../../api/mutations/synchronizePorscheMasterData';
import { useGetModuleOptionsQuery } from '../../../api/queries/getModuleOptions';
import { ModuleFilteringRule, ModuleType } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useExcelUploadModal } from '../../../components/vehicleImportExport';
import { ImportExportType, useModuleSelectModal } from '../../../components/vehicleImportExport/ModuleSelectModal';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { DownloadType, exportVehicles } from '../../../utilities/export';
import hasPermissions from '../../../utilities/hasPermissions';
import renderMarkdown from '../../../utilities/renderMarkdown';
import useGoTo from '../../../utilities/useGoTo';
import ImportImages from './ImportImages';
import VariantList from './VariantList';
import VehicleModuleSelectorModal from './VehicleModuleSelectorModal';

const useSynchronizePorscheMasterData = (companyId: string) => {
    const { t } = useTranslation(['variantList']);
    const [sync] = useSynchronizePorscheMasterDataMutation();

    return useCallback(async () => {
        if (isEmpty(companyId)) {
            return;
        }

        // submitting message
        message.loading({
            content: t('variantList:messages.synchronizing'),
            key: 'primary',
            duration: 0,
        });

        try {
            const { data } = await sync({ variables: { companyId } });
            const result = data?.result;

            const lines: string[] = [];
            const pushSection = (items: string[] | null | undefined, title: string) => {
                if (!isEmpty(items)) {
                    lines.push(title);
                    lines.push(...(items ?? []).map(msg => `- ${msg}`));
                    lines.push('');
                }
            };

            pushSection(result?.errors, t('variantList:messages.synchronizeResult.errors'));
            pushSection(result?.created?.make, t('variantList:messages.synchronizeResult.createdMake'));
            pushSection(result?.created?.model, t('variantList:messages.synchronizeResult.createdModel'));
            pushSection(result?.created?.variant, t('variantList:messages.synchronizeResult.createdVariant'));
            pushSection(result?.updated, t('variantList:messages.synchronizeResult.updated'));

            if (lines.length === 0) {
                lines.push(t('variantList:messages.synchronizeResult.noChange'));
            }

            Modal.info({
                title: t('variantList:messages.synchronizeResult.title'),
                content: renderMarkdown(lines.join('\r\n')),
                onOk: () => {
                    window.location.reload();
                },
            });
        } catch (error) {
            console.error(error);

            message.error({ content: t('variantList:errors.unknown') });
        } finally {
            message.destroy('primary');
        }
    }, [companyId, sync, t]);
};

const VariantListPage = () => {
    const { t } = useTranslation('variantList');
    const { token } = useAccountContext();
    const company = useCompany(true);
    const { displayName, countryCode } = company || {};

    const { data, loading } = useGetModuleOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { kind: ModuleType.SimpleVehicleManagement, companyId: company?.id },
    });

    const excelUploadModal = useExcelUploadModal(DownloadType.Variants);
    const moduleSelectUploadModal = useModuleSelectModal(DownloadType.Variants);

    const goToNewVariantPage = useGoTo('/admin/vehicles/variants/add');

    const showUploadExcelModal = useCallback(() => {
        if (!company) {
            message.warn('Please select a company before uploading.');
        } else if (data?.modules?.length > 1) {
            moduleSelectUploadModal.open(ImportExportType.Import);
        } else if (data?.modules?.length > 0) {
            excelUploadModal.open(data?.modules[0].id);
        }
    }, [company, excelUploadModal, moduleSelectUploadModal, data?.modules]);

    const downloadExcel = useCallback(async () => {
        if (!company) {
            message.warn('Please select a company before uploading.');
        } else if (data?.modules?.length > 1) {
            moduleSelectUploadModal.open(ImportExportType.Export);
        } else if (data?.modules?.length > 0) {
            await exportVehicles(displayName, countryCode, DownloadType.Variants, false, data?.modules[0].id, token);
        }
    }, [company, data?.modules, moduleSelectUploadModal, displayName, countryCode, token]);

    const synchronizePorscheMasterData = useSynchronizePorscheMasterData(company?.id);
    const showSyncButton = useMemo(
        () => !isEmpty(company?.id) && data?.modules?.some(item => item?.porscheMasterDataModuleId),
        [company?.id, data?.modules]
    );

    const hasPermissionsToCreate = useMemo(() => {
        if (data?.modules?.length) {
            return data.modules.some(
                module =>
                    hasPermissions(module.permissions, [permissionKind.createVehicle]) ||
                    hasPermissions(module.permissions, [permissionKind.updateVehicle])
            );
        }

        return false;
    }, [data?.modules]);

    const hasPermissionsToView = useMemo(() => {
        if (data?.modules?.length) {
            return data.modules.some(
                module =>
                    hasPermissions(module.permissions, [permissionKind.viewVehicles]) ||
                    hasPermissions(module.permissions, [permissionKind.updateVehicle]) ||
                    hasPermissions(module.permissions, [permissionKind.createVehicle])
            );
        }

        return false;
    }, [data?.modules]);

    if (!data?.modules || loading) {
        return null;
    }

    const importImageButton = (length: number) => <VehicleModuleSelectorModal isShownSelector={length > 1} />;

    const extra = (
        <>
            {hasPermissionsToCreate && (
                <Button icon={<PlusOutlined />} onClick={goToNewVariantPage} type="primary">
                    {t('variantList:actions.addVariant')}
                </Button>
            )}

            {hasPermissionsToCreate && (
                <Button icon={<UploadOutlined />} onClick={showUploadExcelModal} type="primary">
                    {t('variantList:actions.importVariants')}
                </Button>
            )}
            {hasPermissionsToView && (
                <Button icon={<DownloadOutlined />} onClick={downloadExcel} type="primary">
                    {t('variantList:actions.exportVariants')}
                </Button>
            )}
            {hasPermissionsToCreate && importImageButton(data.modules.length)}
            {showSyncButton && (
                <Button icon={<SyncOutlined />} onClick={synchronizePorscheMasterData} type="primary">
                    {t('variantList:actions.synchronizePorscheMasterData')}
                </Button>
            )}
        </>
    );

    return (
        <ConsolePageWithHeader extra={extra} title={t('variantList:title')}>
            <VariantList />
            {excelUploadModal.render()}
            {moduleSelectUploadModal.render()}
        </ConsolePageWithHeader>
    );
};

export default VariantListPage;
